import { Flex, Text } from '@mantine/core';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	items: { label?: string; value: string }[];
	selectedValue: string;
	onChange: (value: string) => void;
}

const CycleThroughMenu = ({ onChange, items, selectedValue, className }: Props) => {
	const selectedLabel = useMemo(() => {
		const selected = items.find((item) => item.value === selectedValue);
		return selected?.label ?? selectedValue;
	}, [items, selectedValue]);

	const onClick = () => {
		const currentSelectedIndex = items.findIndex((item) => item.value === selectedValue);
		if (currentSelectedIndex == null || currentSelectedIndex === items.length - 1) {
			onChange(items[0].value);
			return;
		}

		const nextValue = items[currentSelectedIndex + 1].value;
		onChange(nextValue);
	};

	return (
		<div className={className} onClick={onClick}>
			<Flex direction="column" gap="0.125rem" justify="center" align="center">
				{items.map(({ value }) => (
					<Dot key={value} active={value === selectedValue} />
				))}
			</Flex>
			<Text variant="B6Medium">{selectedLabel}</Text>
		</div>
	);
};

const Dot = styled.div<{ active: boolean }>`
	width: 0.25rem;
	height: 0.25rem;
	border-radius: 50%;

	background-color: ${({ active, theme }) =>
		active ? theme.v3.colors.blue[6] : theme.v3.colors.gray[4]};
`;

const Memoized = memo(CycleThroughMenu);
const Styled = styled(Memoized)`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	cursor: pointer;
	user-select: none;

	.mantine-Text-root {
		white-space: nowrap;
		font-size: 14px;
	}
`;

export { Styled as CycleThroughMenu };
